// src/hooks/useTaskStatusToggle.ts
// 共用的任務狀態切換 Hook（支援樂觀更新與 Rollback）

import { useState, useCallback } from 'react';
import { Task, TaskStatus } from '../types/task';
import { eventBus, EVENTS } from '../utils/eventBus';
import { logError } from '../utils/logger';

interface UseTaskStatusToggleOptions {
  /**
   * 狀態切換前的回調（可用於本地 UI 更新）
   */
  onBeforeToggle?: (taskId: string, newStatus: TaskStatus) => void;

  /**
   * 狀態切換成功的回調
   */
  onToggleSuccess?: (taskId: string, newStatus: TaskStatus, updatedTask: Task) => void;

  /**
   * 狀態切換失敗的回調（自動 rollback 後觸發）
   */
  onToggleError?: (taskId: string, error: Error) => void;
}

/**
 * 任務狀態切換 Hook
 *
 * 特性：
 * - 樂觀更新：立即更新 UI
 * - 自動同步：IndexedDB + Firestore
 * - 錯誤處理：自動 rollback
 * - EventBus 通知：通知其他組件更新
 */
export function useTaskStatusToggle(userId: string, options?: UseTaskStatusToggleOptions) {
  const [isToggling, setIsToggling] = useState<Map<string, boolean>>(new Map());

  /**
   * 切換任務狀態（OPEN ↔ CLOSED）
   */
  const toggleTaskStatus = useCallback(async (
    task: Task
  ): Promise<boolean> => {
    if (!task || !userId) return false;

    const taskId = task.id;
    const currentStatus = task.status;
    const newStatus: TaskStatus = currentStatus === 'OPEN' ? 'CLOSED' : 'OPEN';

    // 防止重複切換
    if (isToggling.get(taskId)) {
      return false;
    }

    // 設置切換狀態
    setIsToggling(prev => new Map(prev).set(taskId, true));

    try {
      // 1. 樂觀更新：立即通知 UI 層（通過回調）
      options?.onBeforeToggle?.(taskId, newStatus);

      // 2. 動態載入服務（避免循環依賴）
      const [
        { taskService },
        { taskRepository }
      ] = await Promise.all([
        import('../features/task'),
        import('../services/indexedDB/repositories/taskRepository')
      ]);

      // 3. 同步到 IndexedDB（本地優先）
      const updatedTask: Task = {
        ...task,
        status: newStatus,
        updatedAt: new Date(),
        ...(newStatus === 'CLOSED' ? { closedAt: new Date() } : {})
      };

      await taskRepository.createLocal(updatedTask);

      // 4. 背景同步到 Firestore
      try {
        await taskService.updateTaskStatus(taskId, newStatus, userId);

        // 5. 發布 EventBus 事件（通知其他組件）
        eventBus.publish(EVENTS.TASK.STATUS_CHANGED, taskId, newStatus, updatedTask);

        // 6. 成功回調
        options?.onToggleSuccess?.(taskId, newStatus, updatedTask);

        return true;

      } catch (firestoreError) {
        // Firestore 失敗時回滾 IndexedDB
        logError('Failed to sync task status to Firestore', firestoreError);
        await taskRepository.revert(taskId, task);

        // 錯誤回調
        options?.onToggleError?.(
          taskId,
          firestoreError instanceof Error ? firestoreError : new Error('Firestore sync failed')
        );

        return false;
      }

    } catch (error) {
      logError('Failed to toggle task status', error);

      // 錯誤回調
      options?.onToggleError?.(
        taskId,
        error instanceof Error ? error : new Error('Unknown error')
      );

      return false;

    } finally {
      // 清除切換狀態
      setIsToggling(prev => {
        const next = new Map(prev);
        next.delete(taskId);
        return next;
      });
    }
  }, [userId, options, isToggling]);

  /**
   * 檢查特定任務是否正在切換中
   */
  const isTaskToggling = useCallback((taskId: string): boolean => {
    return isToggling.get(taskId) === true;
  }, [isToggling]);

  return {
    toggleTaskStatus,
    isTaskToggling,
  };
}
